import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import * as routes from 'constants/routes';
import BaseWrapper from 'components/layout/BaseWrapper';
import { ButtonRow, ButtonWrapper, CancelButton } from 'components/WalletEntry/styled';
import { useTournament } from 'context/TournamentContext';
import { getTournamentDetails, cancelTournament } from 'api/tournamentService';
import { QRCodeSVG } from 'qrcode.react';
import {
  Card,
  Headline,
  Description,
  QRWrapper,
  SuccessQRBox,
  LinkSection,
  LinkLabel,
  LinkValue,
  ActionsRow,
  ActionButton,
  PlayWrapper,
  PlayButton,
} from './styled';

/**
 * TournamentLobby
 *
 * Mobile-first lobby/share screen shown after tournament creation/start.
 * Two headline variants based on backend status:
 * - status === "new"         -> "Tournament successfully created!"
 * - status === "in_progress" -> "Tournament successfully started!"
 *
 * URL/QR logic, copy/share actions and full Figma-aligned layout
 * will be implemented in follow-up subtasks.
 */
const TournamentLobby: React.FC = () => {
  const navigate = useNavigate();
  const { tournamentDetails, isJoinedToTournamentWithId } = useTournament();
  const [isPlayLoading, setIsPlayLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // TEAM_001: Normalize attributes so TournamentLobby works with both
  // Strapi's { data: { attributes: {...} } } shape and a flattened
  // { data: { status, hash, ... } } shape returned by some endpoints.
  const rawData = tournamentDetails?.data as any;
  const attributes = rawData?.attributes ?? rawData?.data ?? rawData;

  // Raw backend status from Strapi (e.g. "new", "in_progress", "finished")
  const backendStatus = attributes?.status;

  // DS-2 Screen 5 Success copy. We always show "successfully created!"
  // — the in_progress / unknown status branches were copy-only variants
  // that no longer make sense on the share screen, where the user has
  // just published the tournament. Keeping a single headline matches
  // the design and removes a code path the lobby never really needed.
  const headline = (
    <>
      Tournament <span>successfully</span> created!
    </>
  );
  const description =
    'Here is your QR code and dedicated link, you can also find them in the profile section.';

  // Build tournament join URL using the same convention as TournamentDetails
  const tournamentLink = useMemo(() => {
    if (!tournamentDetails?.data) {
      return '';
    }

    const baseUrl = process.env.REACT_APP_API_GAME_API || '';
    const data: any = tournamentDetails.data;
    const attrs = data.attributes ?? data;
    const id = data.id;

    // Require only id + hash; tournament_type may be missing for some backend statuses
    if (!attrs?.hash) {
      return '';
    }

    const type = attrs.tournament_type;

    if (type === 'multiplayer') {
      return `${baseUrl}/join/${id}?hash=${attrs.hash}`;
    }

    if (type === '1vs1') {
      return `${baseUrl}/tournaments/${id}/join?hash=${attrs.hash}`;
    }

    // TEAM_001: Fallback for tournaments without explicit tournament_type
    return `${baseUrl}/tournaments/${id}/join?hash=${attrs.hash}`;
  }, [tournamentDetails]);

  const [copied, setCopied] = useState<'link' | null>(null);

  const handleCopyQr = useCallback(() => {
    if (!tournamentLink || !navigator?.clipboard?.writeText) {
      return;
    }

    navigator.clipboard.writeText(tournamentLink).catch(() => {});
  }, [tournamentLink]);

  const handleCopyLink = useCallback(() => {
    if (!tournamentLink || !navigator?.clipboard?.writeText) {
      return;
    }

    navigator.clipboard
      .writeText(tournamentLink)
      .then(() => {
        setCopied('link');
        setTimeout(() => setCopied(null), 2000);
      })
      .catch(() => {});
  }, [tournamentLink]);

  const handleShareLink = useCallback(() => {
    if (!tournamentLink) {
      return;
    }

    const nav: any = navigator;

    if (nav && typeof nav.share === 'function') {
      nav
        .share({
          title: 'Join my Trading Battles tournament',
          url: tournamentLink,
        })
        .catch(() => {});
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(tournamentLink)
        .then(() => {
          setCopied('link');
          setTimeout(() => setCopied(null), 2000);
        })
        .catch(() => {});
    }
  }, [tournamentLink]);

  const canCancel = backendStatus === 'waiting_for_opponent';

  const handleCancelClick = useCallback(async () => {
    const id = rawData?.id;
    if (!id || isCancelling) return;

    setIsCancelling(true);
    setCancelError(null);

    try {
      await cancelTournament(id);
      if (isMountedRef.current) {
        navigate(routes.TOURNAMENT.replace(':id', String(id)));
      }
    } catch (err: any) {
      console.error('[TournamentLobby] Cancel failed:', err);
      if (isMountedRef.current) {
        setCancelError(err?.message || 'Failed to cancel tournament');
        setIsCancelling(false);
      }
    }
  }, [rawData?.id, isCancelling, navigate]);

  const handlePlayClick = useCallback(async () => {
    const data: any = tournamentDetails?.data;

    if (!data || isPlayLoading) {
      return;
    }

    const id = data.id;

    if (!id) {
      return;
    }

    setIsPlayLoading(true);

    try {
      const details = await getTournamentDetails(id);
      const rawData: any = details?.data;
      const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
      const backendStatus = typeof attributes?.status === 'string'
        ? attributes.status.toLowerCase()
        : '';
      const isTradingStatus = ['in_progress', 'finishing', 'finished'].includes(backendStatus);
      const joined = typeof isJoinedToTournamentWithId === 'function'
        ? await isJoinedToTournamentWithId(rawData?.id ?? id)
        : true;

      if (!joined) {
        // Not a participant — redirect to join page with hash so they get
        // the TournamentsUser screen with "Pay & Join" / "Join" flow
        const hash = attributes?.hash;
        const joinPath = routes.TOURNAMENTS.replace(':id', String(rawData?.id ?? id));
        navigate(hash ? `${joinPath}?hash=${hash}` : joinPath, {
          state: { tournamentDetails: details },
        });
      } else {
        const targetRoute = isTradingStatus
          ? routes.TOURNAMENT
          : routes.STATUSTOURNAMENT;
        navigate(targetRoute.replace(':id', String(rawData?.id ?? id)), {
          state: { tournamentDetails: details },
        });
      }
    } catch (error) {
      console.error('[TournamentLobby] Failed to refresh tournament before play:', error);
      navigate(routes.TOURNAMENT.replace(':id', String(id)));
    } finally {
      if (isMountedRef.current) {
        setIsPlayLoading(false);
      }
    }
  }, [navigate, tournamentDetails, isJoinedToTournamentWithId, isPlayLoading]);

  return (
    <BaseWrapper compact>
      <Card data-testid="tournament-lobby" aria-labelledby="tournament-lobby-headline">
        <Headline id="tournament-lobby-headline">{headline}</Headline>
        <Description>{description}</Description>

        {tournamentLink && (
          <>
            <QRWrapper>
              <SuccessQRBox>
                <QRCodeSVG
                  data-testid="tournament-qr"
                  value={tournamentLink}
                  size={192}
                  level="H"
                  bgColor="#15191e"
                  fgColor="#52D381"
                  includeMargin={false}
                />
              </SuccessQRBox>
            </QRWrapper>

            <ActionsRow className="copy-qr-row">
              <ActionButton type="button" onClick={handleCopyQr}>
                Copy QR code
              </ActionButton>
            </ActionsRow>

            <LinkSection>
              <LinkLabel>Dedicated link:</LinkLabel>
              <LinkValue data-testid="dedicated-link">{tournamentLink}</LinkValue>
              <ActionsRow>
                <ActionButton type="button" onClick={handleCopyLink}>
                  {copied === 'link' ? 'Copied!' : 'Copy link'}
                </ActionButton>
                <ActionButton type="button" onClick={handleShareLink}>
                  Share link
                </ActionButton>
              </ActionsRow>
            </LinkSection>

            {cancelError && (
              <Description style={{ color: '#FF6782', marginBottom: 8 }}>{cancelError}</Description>
            )}

            {canCancel ? (
              <ButtonRow>
                <ButtonWrapper>
                  <CancelButton
                    type="button"
                    onClick={handleCancelClick}
                    disabled={isCancelling}
                  >
                    {isCancelling ? 'Cancelling...' : 'Cancel'}
                  </CancelButton>
                </ButtonWrapper>
                <ButtonWrapper>
                  <PlayButton
                    type="button"
                    onClick={handlePlayClick}
                    disabled={isPlayLoading}
                  >
                    Play
                  </PlayButton>
                </ButtonWrapper>
              </ButtonRow>
            ) : (
              <PlayWrapper>
                <PlayButton
                  type="button"
                  onClick={handlePlayClick}
                  disabled={isPlayLoading}
                >
                  Play
                </PlayButton>
              </PlayWrapper>
            )}
          </>
        )}
      </Card>
    </BaseWrapper>
  );
}

export default TournamentLobby;
